import React, { useEffect, useRef, useState } from 'react';
import {
  AppConstants,
  AppRouteUi,
  CallLogType,
  CallStatusType,
  topLoaderProgress,
} from '../../configs';
import {
  CallLogResponseModel,
  CallSummaryResponseModel,
} from '../../models/api/Call';
import { AssetSvg } from '../../assets/svg/index';
import { useFunctionalityCallSummary } from '.';
import { ColumnSelectionsModel } from '../../models';
import { ColumnToolTip } from '../../features';
import { handleNumberToTwoDecimalPoint } from '../../utils';
import { useAppSelector, VRIAppStateType } from '../../core';
import { Link } from 'react-router-dom';

type Prop = {
  userRole: string | '';
  setLoaderProgress?: (value: number) => void;
  setCallSummaryData?: (value: CallSummaryResponseModel) => void;
  setShow: (value: boolean) => void;
  callLogType: CallLogType;
  columnSelection?: ColumnSelectionsModel[];
  onOpenRecording?: (row: CallLogResponseModel) => void;
};
export function useFunctionalityCallLogColumns(prop: Prop) {
  const {
    userRole,
    setCallSummaryData,
    setLoaderProgress,
    setShow,
    callLogType,
    columnSelection,
    onOpenRecording,
  } = prop;

  const fromOADate = (oadate: number) => {
    const millis = (oadate - 25569) * 86400000;

    const tzOffset = new Date().getTimezoneOffset() * 60000;

    return new Date(millis + tzOffset);
  };


  const formatDuration = (totalDays: number | null | undefined): string => {
    if (!totalDays) return '-';
    const seconds = totalDays * 86400;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };



  const { LoadById } = useFunctionalityCallSummary();
  const compactIconStyle = { width: '15px' };
  const rejectedIconStyle = { width: '16px' };
  const operatorNotFoundIconStyle = { width: '16px' };
  const abandonedIconStyle = { width: '17px' };

  const callLogIcon = (row: CallLogResponseModel) => {
    const isConferenceCall =
      !!row.outboundCaller || !!(row.operators?.length && row.operators.length > 1);

    const getCallStatusLabel = () => {
      if (row.callStatusType === CallStatusType.Missed) {
        return 'Missed call';
      }

      if (row.callStatusType === CallStatusType.Rejected) {
        return 'Rejected call';
      }

      if (row.callStatusType === CallStatusType.Canceled) {
        return 'Canceled call';
      }

      if (row.callStatusType === CallStatusType.OperatorNotFound) {
        return 'Operator not found';
      }

      if (row.callStatusType === CallStatusType.VRIOperatorNotFound) {
        return 'VRI operator not found';
      }

      if (row.callStatusType === CallStatusType.AbandonedAuthFailure) {
        return 'Abandoned call auth failure';
      }

      if (row.callStatusType === CallStatusType.AbandonedLanguageFailure) {
        return 'Abandoned call language failure';
      }

      return 'Completed call';
    };

    const tooltipLabel = isConferenceCall
      ? `Conference ${getCallStatusLabel().toLowerCase()}`
      : getCallStatusLabel();

    const renderConferenceIcon = (icon: JSX.Element) => {
      const content = isConferenceCall
        ? <div className="conference-tableicon-wrapper"><span>c</span>{icon}</div>
        : icon;

      return (
        <span title={tooltipLabel} aria-label={tooltipLabel}>
          {content}
        </span>
      );
    };

    if (
      row.callStatusType === CallStatusType.Missed
    ) {
      return renderConferenceIcon(<AssetSvg.MissedCall style={compactIconStyle} />);
    } else if (row.callStatusType === CallStatusType.Rejected) {
      return renderConferenceIcon(
        <AssetSvg.RejectedCall style={rejectedIconStyle} />
      );
    } else if (row.callStatusType === CallStatusType.Canceled) {
      return renderConferenceIcon(
        <AssetSvg.CanceledCall style={compactIconStyle} />
      );
    } else if (row.callStatusType === CallStatusType.OperatorNotFound) {
      return renderConferenceIcon(
        <AssetSvg.OperatorNotFound style={operatorNotFoundIconStyle} />
      );
    } else if (row.callStatusType === CallStatusType.VRIOperatorNotFound) {
      return renderConferenceIcon(
        <AssetSvg.VRIOperatorNotFound style={operatorNotFoundIconStyle} />
      );
    } else if (row.callStatusType === CallStatusType.AbandonedAuthFailure) {
      return renderConferenceIcon(
        <AssetSvg.AbandonedAuth style={abandonedIconStyle} />
      );
    } else if (row.callStatusType === CallStatusType.AbandonedLanguageFailure) {
      return renderConferenceIcon(
        <AssetSvg.AbandonedLanguage style={abandonedIconStyle} />
      );
    }

    return renderConferenceIcon(<AssetSvg.AcceptCallSuccess />);
  };
  const companyName = useAppSelector(
    (state: VRIAppStateType) => state.auth.profile?.companyName
  );
  const isDarkMode =
    document.documentElement.getAttribute('data-mode') === 'dark';
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [fadeOut, setFadeOut] = useState(false);
  const [hoveredCopyKey, setHoveredCopyKey] = useState<string | null>(null);
  const copiedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const copyToClipboard = async (
    event: React.MouseEvent,
    value?: string | null,
    key?: string
  ) => {
    event.stopPropagation();
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
      }
      setCopiedKey(key ?? null);
      setFadeOut(false);
      window.setTimeout(() => setFadeOut(true), 400);
      copiedTimerRef.current = window.setTimeout(() => {
        setCopiedKey(null);
        setFadeOut(false);
      }, 1800);
    } catch (error) {
      console.error('Failed to copy value to clipboard', error);
    }
  };

  const renderCopyableValue = (value?: string | null, key?: string) => {
    const hasValue = !!value;

    return (
      <div
        className="d-flex align-items-center w-100"
        data-tag="allowRowEvents"
        style={{ minWidth: 0, gap: '6px' }}
      >
        <span
          title={value ?? '-'}
          data-tag="allowRowEvents"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value ?? '-'}
        </span>
        {hasValue ? (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              className="btn svg-color"
              title="Copy"
              aria-label={`Copy ${value}`}
              style={{
                flexShrink: 0,
                width: '26px',
                height: '26px',
                padding: '0',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow:
                  hoveredCopyKey === key
                    ? '0 0 0 1px rgba(123, 97, 168, 0.2), 0 2px 6px rgba(0, 0, 0, 0.12)'
                    : 'none',
                transition: 'box-shadow 120ms ease',
              }}
              onMouseEnter={() => setHoveredCopyKey(key ?? null)}
              onMouseLeave={() => setHoveredCopyKey((current) => (current === key ? null : current))}
              onFocus={() => setHoveredCopyKey(key ?? null)}
              onBlur={() => setHoveredCopyKey((current) => (current === key ? null : current))}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                void copyToClipboard(event, value, key);
              }}
            >
              <AssetSvg.CopyPaste style={{ width: 14, height: 14 }} />
            </button>
            <span
              className={`fade-out-pill ${copiedKey === key && fadeOut ? 'fade-out-pill-hidden' : ''}`}
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '6px',
                padding: '4px 8px',
                fontSize: '11px',
                fontWeight: 500,
                backgroundColor: isDarkMode ? '#1f2128' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#1f2128',
                border: isDarkMode
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(31, 33, 40, 0.12)',
                borderRadius: '999px',
                boxShadow: isDarkMode
                  ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                  : '0 2px 8px rgba(31, 33, 40, 0.12)',
                whiteSpace: 'nowrap',
                visibility: copiedKey === key ? 'visible' : 'hidden',
                zIndex: 2,
              }}
            >
              copied to clipboard!
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  const rolewiseColumn = [
    {
      name: ' ',
      width: '45px',
      cell: (row: CallLogResponseModel) => (
        <div
          data-tag="allowRowEvents"
          style={{ overflow: 'visible', position: 'relative' }}
        >
          {callLogIcon(row)}
        </div>
      ),
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CallStatusType'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Id'
            )?.name
          }
        />
      ),
      width: '140px',
      cell: (row: CallLogResponseModel) => {
        return renderCopyableValue(row.id, `${row.id}-id`);
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Id'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Sid'
            )?.name
          }
        />
      ),
      width: '140px',
      cell: (row: CallLogResponseModel) => {
        return renderCopyableValue(row.sid, `${row.id}-sid`);
      },
      omit:
        userRole === AppConstants.UserRoles.Admin
          ? columnSelection?.find(
            (eachColumn) => eachColumn.propertyName === 'Sid'
          )?.isEnabled
            ? false
            : true
          : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Date'
            )?.name
          }
        />
      ),
      width: '120px',
      selector: (row: CallLogResponseModel) => {
        const dateValue = row.date ? fromOADate(Number(row.date)) : null;
        return (
          <ColumnToolTip
            data={
              dateValue
                ? dateValue.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
                : "-"
            }
          />
        );
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Date'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CallerName'
            )?.name
          }
        />
      ),
      //  width: "120px",
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={row.callerName ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CallerName'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CallerPhone'
            )?.name
          }
        />
      ),
      width: "130px",
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={row.callerPhone ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CallerPhone'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CompanyName'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={row.companyName ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CompanyName'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'ViaResellerName'
            )?.name
          }
        />
      ),
      minWidth: '130px',
      selector: (row: CallLogResponseModel) => {
        return (
          <>
            {row.viaResellerName !== companyName &&
              userRole === AppConstants.UserRoles.Admin ? (
              <Link
                data-tag="allowRowEvents"
                to={AppRouteUi.Reseller.Profile.Load(row.viaResellerId ?? '')}
              >
                <ColumnToolTip data={row.viaResellerName ?? '-'} />
              </Link>
            ) : (
              <ColumnToolTip data={row.viaResellerName ?? '-'} />
            )}
          </>
        );
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'ViaResellerName'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'ToResellerName'
            )?.name
          }
        />
      ),
      width: '120px',
      selector: (row: CallLogResponseModel) => {
        return (
          <>
            {row.toResellerName !== companyName &&
              userRole === AppConstants.UserRoles.Admin ? (
              <Link
                data-tag="allowRowEvents"
                to={AppRouteUi.Reseller.Profile.Load(row.toResellerId ?? '')}
              >
                <ColumnToolTip data={row.toResellerName ?? '-'} />
              </Link>
            ) : (
              <ColumnToolTip data={row.toResellerName ?? '-'} />
            )}
          </>
        );
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'ToResellerName'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Location'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.location ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Location'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Department'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.department ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Department'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CustomField1'
            )?.name
          }
        />
      ),
      minWidth: '140px',
      selector: (row: CallLogResponseModel) => row.customField1 ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CustomField1'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CustomField2'
            )?.name
          }
        />
      ),
      minWidth: '140px',
      selector: (row: CallLogResponseModel) => row.customField2 ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CustomField2'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CustomField3'
            )?.name
          }
        />
      ),
      minWidth: '140px',
      selector: (row: CallLogResponseModel) => row.customField3 ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CustomField3'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'CustomField4'
            )?.name
          }
        />
      ),
      minWidth: '140px',
      selector: (row: CallLogResponseModel) => row.customField4 ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'CustomField4'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Language'
            )?.name
          }
        />
      ),
      width: '100px',
      selector: (row: CallLogResponseModel) => {
        const language =
          row.operatorSpecificLanguage != null
            ? row.operatorSpecificLanguage
            : row.language;

        return <ColumnToolTip data={language ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Language'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Skill'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.skill ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Skill'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Gender'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.gender ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Gender'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'StartTime'
            )?.name
          }
        />
      ),
      width: '100px',
      selector: (row: CallLogResponseModel) => {
        if (userRole === AppConstants.UserRoles.Operator) {
          return <ColumnToolTip data={row.opStartTime ?? '-'} />;
        }
        else return <ColumnToolTip data={row.startTime ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'StartTime'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'EndTime'
            )?.name
          }
        />
      ),
      width: '100px',
      selector: (row: CallLogResponseModel) => {
        if (userRole === AppConstants.UserRoles.Operator) {
          return <ColumnToolTip data={row.opEndTime ?? '-'} />;
        }
        else return <ColumnToolTip data={row.endTime ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'EndTime'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Duration'
            )?.name
          }
        />
      ),
      width: '100px',
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={formatDuration(Number(row.duration))} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Duration'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Responded'
            )?.name
          }
        />
      ),
      width: '110px',
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={formatDuration(Number(row.responded))} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Responded'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Device'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.device ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Device'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Platform'
            )?.name
          }
        />
      ),
      width: '90px',
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={row.platform ?? '-'} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Platform'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'ConnectionFee'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.connectionFee!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.connectionFee! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'ConnectionFee'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'TechnologyFee'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.technologyFee!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.technologyFee! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'TechnologyFee'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Revenue'
            )?.name
          }
        />
      ),
      width: '90px',
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.revenue!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.revenue! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Revenue'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Profit'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.profit!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.profit! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Profit'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'ViaResellerProfit'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.viaResellerProfit!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.viaResellerProfit! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'ViaResellerProfit'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'ToResellerProfit'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.toResellerProfit!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.toResellerProfit! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'ToResellerProfit'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'TotalBill'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.totalBill!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.totalBill! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'TotalBill'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'Operators'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={row.operators?.length ? row.operators?.join(', ') : '-'}
          />
        );
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'Operators'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'OperatorsEarning'
            )?.name
          }
        />
      ),
      // width: "100px",
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.operatorsEarning!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.operatorsEarning! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'OperatorsEarning'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'VriCharges'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.vriCharges!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.vriCharges! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'VriCharges'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'VriEarnings'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => {
        return (
          <ColumnToolTip
            data={handleNumberToTwoDecimalPoint(row.vriEarnings!) ?? '-'}
          />
        );
      },
      conditionalCellStyles: [
        {
          when: (row: CallLogResponseModel) => row.vriEarnings! < 0,
          style: {
            color: 'red',
          },
        },
      ],
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'VriEarnings'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'OutboundCaller'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.outboundCaller ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'OutboundCaller'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'OutboundDuration'
            )?.name
          }
        />
      ),
      // selector: (row: CallLogResponseModel) => row.outboundDuration ?? '-',
      selector: (row: CallLogResponseModel) => {
        return <ColumnToolTip data={formatDuration(Number(row.outboundDuration))} />;
      },
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'OutboundDuration'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: (
        <ColumnToolTip
          data={
            columnSelection?.find(
              (eachColumn) => eachColumn.propertyName === 'OutboundBill'
            )?.name
          }
        />
      ),
      selector: (row: CallLogResponseModel) => row.outboundBill ?? '-',
      omit: columnSelection?.find(
        (eachColumn) => eachColumn.propertyName === 'OutboundBill'
      )?.isEnabled
        ? false
        : true,
    },
    {
      name: <ColumnToolTip data={'Actions'} />,
      allowOverflow: true,
      ignoreRowClick: true,
      button: true,
      conditionalCellStyles: [
        {
          when: () => true,
          style: {
            justifyContent: 'flex-start',
            paddingLeft: "20px"
          },
        },
      ],
      cell: (row: CallLogResponseModel) => {
        return (
          <div
            className="d-flex align-items-center"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div
              className="btn p-0 mr-4 svg-color"
              title="View"
              onClick={() => {
                if (setLoaderProgress)
                  setLoaderProgress(topLoaderProgress.start);
                LoadById(row.id).then((data) => {
                  if (setLoaderProgress)
                    setLoaderProgress(topLoaderProgress.complete);
                  if (setCallSummaryData && data) setCallSummaryData(data);
                  setShow(true);
                });
              }}
            >
              <span>
                <AssetSvg.ViewProfile />
              </span>
            </div>
              {(row.hasRecord || row.hasTranscription) &&
              userRole === AppConstants.UserRoles.Admin ? (
                <div
                  className="btn p-0 m-0 svg-color me-2"
                  title="Recording"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenRecording?.(row);
                  }}
                >
                  <span>
                    <AssetSvg.MediaPlay style={{ width: 15, height: 15 }} />
                  </span>
                </div>
              ) : null}
          </div>
        );
      },
    },
  ];

  return {
    callLogColumn: rolewiseColumn,
  };
}
